import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, message, Table, DatePicker, Select } from 'antd';
import { getRangeOptions, getPaginationDetails, getDefaultPageSize } from './reports.module';
import { getAPIDateFormat, convertDateStringToDate, getLocalDate, getLocalDateAndTime } from './../../utils/date.utils';
import { ScheduleSelect } from './../../components/search-components/schedule-select/schedule-select.component';
import { isPvtLtdCompany, isValid, isValidArray, isValidBool } from "../../utils/utilities";
import { addFloatingPoints as AFP, subtractFloatingPoints as SFP, roundNumber } from './../../utils/math.utils';
import { getVechicles } from './../../components/edit-vechicle-component/edit-vechicle.module';
import { SHARED_TRIP_SALES_URL } from './../../utils/api-url';
import { postCall } from './../../utils/network.util';
import * as XLSX from 'xlsx';
import './sales-ticket.report.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const allowedMaxDaysRange = 31;

// ============================================================
// Formatters
// ============================================================
const fmtINR = (n) => {
    if (n == null || isNaN(n)) return '—';
    const num = Number(n);
    const hasDecimal = num % 1 !== 0;
    return '₹' + num.toLocaleString('en-IN', hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {});
};
const fmtINRpaise = (n) => {
    if (n == null || isNaN(n)) return '—';
    return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const getChannelClass = (name) => {
    if (!name) return 'ch-web';
    const n = String(name).toLowerCase();
    if (n.includes('redbus')) return 'ch-redbus';
    if (n.includes('abhibus')) return 'ch-abhibus';
    if (n.includes('paytm')) return 'ch-paytm';
    if (n.includes('website')) return 'ch-website';
    return 'ch-web';
};

// ============================================================
// Inline icons (same as the sales report)
// ============================================================
const Icon = {
    search: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/></svg>,
    download: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 2v9m-3-3 3 3 3-3M2 13h12"/></svg>,
    caretRight: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 4 4 4-4 4"/></svg>,
    run: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 3l8 5-8 5z"/></svg>,
};

// ============================================================
// Data fetch
// ============================================================
const fetchSharedTripSalesSheet = (fromDate, toDate, isBooking, onSuccess = () => {}, onFailure = () => {}) => {
    var fetched = postCall(SHARED_TRIP_SALES_URL, {
        fromDate: fromDate,
        toDate: toDate,
        isBooking: isBooking,
        operatorCode: global.operatorCode
    });
    fetched.then((data) => {
        if (data.status === "SUCCESS" && data.payload.Status === "SUCCESS") {
            onSuccess(data.payload.Payload);
        } else {
            onFailure("Failed to get shared trip sales");
        }
    }).catch(() => {
        onFailure("Failed to get shared trip sales");
    });
};

// ============================================================
// Preserved helpers (same behavior as the sales report)
// ============================================================
const filterValidation = (filters, ticketAgentName) => {
    let { isSelectAllAgents, agentName } = filters;
    if (isSelectAllAgents) return true;
    if (isValidArray(agentName) && agentName.length > 0) return agentName.includes(ticketAgentName);
    return false;
};

const getAgentFilterOptions = (tickets) => {
    if (!isValidArray(tickets) || tickets.length === 0) return [];
    const agentNames = new Set();
    tickets.forEach(ticket => { if (isValid(ticket.name)) agentNames.add(ticket.name); });
    return Array.from(agentNames).sort().map(name => ({ text: name, value: name }));
};

const getSubAgentFilterOptions = (tickets) => {
    if (!isValidArray(tickets) || tickets.length === 0) return [];
    const names = new Set();
    tickets.forEach(t => { if (isValid(t.sub_agent_name)) names.add(t.sub_agent_name); });
    return Array.from(names).sort().map(name => ({ text: name, value: name }));
};

const getOperatorFilterOptions = (tickets) => {
    if (!isValidArray(tickets) || tickets.length === 0) return [];
    const names = new Set();
    tickets.forEach(t => { if (isValid(t.operator_name)) names.add(t.operator_name); });
    return Array.from(names).sort().map(name => ({ text: name, value: name }));
};

const generateJsonToExport = (filters, confirmedTickets, cancelledTickets, confirmColumns, cancelColumns) => {
    let reportsConfirmedXmlData = [];
    let reportsCancelledXmlData = [];

    const renderForExport = (column) => column.exportRender || column.render;
    const titleForExport = (column) => isValid(column.excelTitle) ? column.excelTitle : (typeof column.title === 'string' ? column.title : column.key);

    const confirmHeaders = confirmColumns.map(titleForExport);
    const cancelHeaders = cancelColumns.map(titleForExport);

    if (isValidArray(confirmedTickets) && confirmedTickets.length > 0) {
        confirmedTickets.forEach((element, index) => {
            if (filterValidation(filters, element.name)) {
                let dataColumn = [];
                confirmColumns.forEach(column => {
                    const r = renderForExport(column);
                    dataColumn.push(r ? r(element[column.dataIndex], element, index) : '');
                });
                reportsConfirmedXmlData.push(dataColumn);
            }
        });
        const confirmedSummary = calculateSummary(confirmedTickets, confirmColumns);
        if (confirmedSummary) {
            let summaryRow = [];
            confirmColumns.forEach((column, index) => {
                if (index === 0) summaryRow.push('Total');
                else if (column.dataIndex && confirmedSummary[column.dataIndex] !== undefined) summaryRow.push(roundNumber(confirmedSummary[column.dataIndex]));
                else if (column.key && confirmedSummary[column.key] !== undefined) summaryRow.push(roundNumber(confirmedSummary[column.key]));
                else summaryRow.push('');
            });
            reportsConfirmedXmlData.push(summaryRow);
        }
    }

    if (isValidArray(cancelledTickets) && cancelledTickets.length > 0) {
        cancelledTickets.forEach((element, index) => {
            if (filterValidation(filters, element.name)) {
                let dataColumn = [];
                cancelColumns.forEach(column => {
                    const r = renderForExport(column);
                    dataColumn.push(r ? r(element[column.dataIndex], element, index) : '');
                });
                reportsCancelledXmlData.push(dataColumn);
            }
        });
        const cancelledSummary = calculateSummary(cancelledTickets, cancelColumns);
        if (cancelledSummary) {
            let summaryRow = [];
            cancelColumns.forEach((column, index) => {
                if (index === 0) summaryRow.push('Total');
                else if (column.dataIndex && cancelledSummary[column.dataIndex] !== undefined) summaryRow.push(roundNumber(cancelledSummary[column.dataIndex]));
                else if (column.key && cancelledSummary[column.key] !== undefined) summaryRow.push(roundNumber(cancelledSummary[column.key]));
                else summaryRow.push('');
            });
            reportsCancelledXmlData.push(summaryRow);
        }
    }

    let agentName = filters.isSelectAllAgents ? 'all' : (isValidArray(filters.agentName) ? filters.agentName.join(', ') : '');
    let dateType = filters.isBookingDate ? "Booking" : "Travel";
    let generatedTime = getLocalDateAndTime(new Date());
    var worksheetArray = [];
    worksheetArray.push([]);
    worksheetArray.push([]);
    worksheetArray.push(["Report", "Agents", "From Date", "To Date", "Type of date", "Generated day/time"]);
    worksheetArray.push(["Shared trip sales report", agentName, filters.fromDate, filters.toDate, dateType, generatedTime]);
    worksheetArray.push([]);
    worksheetArray.push(confirmHeaders);
    worksheetArray.push(...reportsConfirmedXmlData);
    worksheetArray.push([]);
    worksheetArray.push([]);
    worksheetArray.push(["Cancelled Tickets"]);
    worksheetArray.push(cancelHeaders);
    worksheetArray.push(...reportsCancelledXmlData);

    var worksheet = XLSX.utils.aoa_to_sheet(worksheetArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shared Trip Sales');
    XLSX.writeFile(workbook, 'shared-trip-sales-report.xlsx');
};

const getTicketsByAgentName = (tickets, agentName, isSelectAllAgents, vehicleFilter) => {
    if (!isValidArray(tickets)) return [];
    const hasAgentFilter = isValidArray(agentName) && agentName.length > 0;
    const vehicleArray = Array.isArray(vehicleFilter)
        ? vehicleFilter
        : (isValid(vehicleFilter) ? [vehicleFilter] : []);
    const hasVehicleFilter = vehicleArray.length > 0;
    const matchesVehicle = (vid) => hasVehicleFilter && vehicleArray.some(v => v == vid);
    const allfilterApplied = !isSelectAllAgents && hasAgentFilter && hasVehicleFilter;

    if (isSelectAllAgents && !hasVehicleFilter) return tickets;

    const formatedTickets = [];
    if (tickets.length > 0 && (hasAgentFilter || hasVehicleFilter)) {
        tickets.forEach(element => {
            if (allfilterApplied) {
                if (hasAgentFilter && agentName.includes(element.name) && matchesVehicle(element.vehicle_id)) formatedTickets.push(element);
            } else {
                if (!isSelectAllAgents && hasAgentFilter && agentName.includes(element.name)) formatedTickets.push(element);
                if (hasVehicleFilter && matchesVehicle(element.vehicle_id)) formatedTickets.push(element);
            }
        });
    }
    return formatedTickets;
};

const getTicketsByOperator = (tickets, operatorNames, isSelectAllOperators) => {
    if (!isValidArray(tickets)) return [];
    if (isSelectAllOperators || !isValidArray(operatorNames) || operatorNames.length === 0) return tickets;
    return tickets.filter(ticket => operatorNames.includes(ticket.operator_name));
};

const getTicketsBySchedule = (tickets, scheduleId) => {
    if (!isValidArray(tickets)) return [];
    if (!isValid(scheduleId)) return tickets;
    return tickets.filter(ticket => ticket.schedule_id === scheduleId);
};

const calculateSummary = (data, columns) => {
    if (!data || data.length === 0) return null;
    const summary = {};
    columns.forEach(column => {
        if (column.dataIndex) {
            if (typeof data[0][column.dataIndex] == 'number') {
                summary[column.dataIndex] = data.reduce((acc, curr) => AFP(acc, curr[column.dataIndex] || 0), 0);
            }
        } else if (column.key) {
            switch (column.key) {
                case 'gst':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, AFP(curr.cgst || 0, curr.sgst || 0)), 0); break;
                case 'net_commision':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, SFP(curr.commission || 0, curr.tds || 0)), 0); break;
                case 'refund_amount':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, SFP(curr.amountPaid || 0, curr.cancellation_penalty || 0)), 0); break;
                default: break;
            }
        }
    });
    return summary;
};

// ============================================================
// Summary rows — commission centric for the booking (child) operator
// ============================================================
const buildSummaryRows = (filteredConfirmed, filteredCancelled) => {
    const sumFare = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t.fare || 0), 0) : 0;
    const sumGst = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, AFP(t.cgst || 0, t.sgst || 0)), 0) : 0;
    const sumDirectDiscount = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t.direct_discount || 0), 0) : 0;
    const sumField = (rows, field) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t[field] || 0), 0) : 0;
    const sumNetCommission = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, SFP(t.commission || 0, t.tds || 0)), 0) : 0;

    const cFare = sumFare(filteredConfirmed);
    const xFare = sumFare(filteredCancelled);
    const cGst = sumGst(filteredConfirmed);
    const xGst = sumGst(filteredCancelled);
    const cTotal = AFP(cFare, cGst);
    const xTotal = AFP(xFare, xGst);
    const cDisc = sumDirectDiscount(filteredConfirmed);
    const xDisc = sumDirectDiscount(filteredCancelled);
    const cPaid = SFP(cTotal, cDisc);
    const xPaid = SFP(xTotal, xDisc);
    const cComm = sumField(filteredConfirmed, 'commission');
    const xComm = sumField(filteredCancelled, 'commission');
    const cTds = sumField(filteredConfirmed, 'tds');
    const xTds = sumField(filteredCancelled, 'tds');
    const cNetComm = sumNetCommission(filteredConfirmed);
    const xNetComm = sumNetCommission(filteredCancelled);

    const revenue = [
        { code: 'A', metric: 'Ticket Fare', confirmed: cFare, cancelled: xFare, relevant: SFP(cFare, xFare) },
        { code: 'B', metric: 'GST', confirmed: cGst, cancelled: xGst, relevant: SFP(cGst, xGst) },
        { code: 'C', metric: 'Total Amount (A + B)', confirmed: cTotal, cancelled: xTotal, relevant: SFP(cTotal, xTotal), subtotal: true },
        { code: 'D', metric: 'Discount given to customers', confirmed: cDisc, cancelled: xDisc, relevant: SFP(cDisc, xDisc) },
        { code: 'E', metric: 'Total amount paid by customers (C − D)', confirmed: cPaid, cancelled: xPaid, relevant: SFP(cPaid, xPaid), subtotal: true },
    ];
    const commissions = [
        { code: 'F', metric: 'Commission earned', confirmed: cComm, cancelled: xComm, relevant: SFP(cComm, xComm) },
        { code: 'G', metric: 'TDS deducted from commission', confirmed: cTds, cancelled: xTds, relevant: SFP(cTds, xTds) },
        { code: 'H', metric: 'Net Commission (F − G)', confirmed: cNetComm, cancelled: xNetComm, relevant: SFP(cNetComm, xNetComm), subtotal: true },
    ];

    const fromCustomers = SFP(cPaid, xPaid);
    const grossCommission = SFP(cComm, xComm);
    const netCommissionRelevant = SFP(cNetComm, xNetComm);

    return { revenue, commissions, fromCustomers, grossCommission, netCommissionRelevant };
};

// ============================================================
// Sub-components (same classes as the sales report so styles match)
// ============================================================
function PageHeader({ onExport, canExport }) {
    return (
        <div className="pageheader">
            <div>
                <div className="breadcrumb">
                    <span>Reports</span>{Icon.caretRight}<span style={{ color: 'var(--ink-2)' }}>Shared trip sales</span>
                </div>
                <h1>Shared Trip Sales</h1>
            </div>
            <div className="pageheader-actions">
                <button type="button" className="btn primary" disabled={!canExport} onClick={onExport}>
                    {Icon.download}Export
                </button>
            </div>
        </div>
    );
}

function FilterCard({
    form, schedules,
    dateType, setDateType,
    onSubmit, onReset, onValuesChange, running,
}) {
    const handleSubmit = (values) => onSubmit({ ...values, isBooking: dateType });

    return (
        <div className="card">
            <Form
                form={form}
                onFinish={handleSubmit}
                onValuesChange={(changed, all) => onValuesChange(changed, { ...all, isBooking: dateType })}
            >
                <div className="filter-grid">
                    <div className="field" style={{ gridColumn: '1 / span 2', gridRow: 1 }}>
                        <div className="field-label">Date type</div>
                        <div className="segmented">
                            <button type="button"
                                className={dateType === true ? 'active' : ''}
                                onClick={() => setDateType(true)}>Booking</button>
                            <button type="button"
                                className={dateType === false ? 'active' : ''}
                                onClick={() => setDateType(false)}>Travel</button>
                        </div>
                    </div>

                    <div className="field field-rangepicker-wrap" style={{ gridColumn: '3 / span 4', gridRow: 1 }}>
                        <div className="field-label">Date range</div>
                        <Form.Item name="dateRange" rules={[
                            { required: true, message: 'Please select a date range' },
                            () => ({
                                validator(_, value) {
                                    if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) return Promise.resolve();
                                    return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                },
                            }),
                        ]}>
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} format="DD MMM YYYY" />
                        </Form.Item>
                    </div>

                    <div className="field field-select-wrap" style={{ gridColumn: '7 / span 6', gridRow: 1 }}>
                        <div className="field-label">Schedule</div>
                        <ScheduleSelect
                            schedules={schedules || []}
                            optionKey="id"
                            showLabel={false}
                            isManditory={false}
                            fieldName="returnId"
                        />
                    </div>

                    <div className="filter-actions" style={{ gridColumn: '1 / -1', gridRow: 2, display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                        <button type="button" className="btn" onClick={onReset} disabled={running}>Reset</button>
                        <button type="submit" className="btn primary" disabled={running}>
                            {Icon.run}{running ? 'Running…' : 'Run report'}
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}

function RefineBar({
    agentOptions, selectedAgents, isSelectAllAgents, onAgentChange,
    vehicleOptions, selectedVehicles, isSelectAllVehicles, onVehicleChange,
    operatorOptions, selectedOperators, isSelectAllOperators, onOperatorChange,
}) {
    const allAgents = useMemo(() => {
        if (!isValidArray(agentOptions)) return [];
        return [...new Set(agentOptions.map(a => (a && a.name) || a).filter(Boolean))].sort();
    }, [agentOptions]);

    const allVehicleIds = useMemo(
        () => isValidArray(vehicleOptions) ? vehicleOptions.map(v => v.id) : [],
        [vehicleOptions]
    );

    const handleAgentChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allAgents.length > 0 && selected.length >= allAgents.length);
        onAgentChange({
            agentName: selectAll ? allAgents : selected,
            isSelectAllAgents: selectAll,
        });
    };

    const handleVehicleChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allVehicleIds.length > 0 && selected.length >= allVehicleIds.length);
        onVehicleChange({
            vehicleNumbers: selectAll ? allVehicleIds : selected,
            isSelectAllVehicles: selectAll,
        });
    };

    // Operator filter is shown only when the result has bookings on multiple operators' trips
    const allOperators = isValidArray(operatorOptions) ? operatorOptions : [];
    const showOperatorFilter = allOperators.length > 1;

    const handleOperatorChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allOperators.length > 0 && selected.length >= allOperators.length);
        onOperatorChange({
            operatorNames: selectAll ? allOperators : selected,
            isSelectAllOperators: selectAll,
        });
    };

    const agentValue = isSelectAllAgents ? [] : (selectedAgents || []);
    const vehicleValue = isSelectAllVehicles ? [] : (selectedVehicles || []);
    const operatorValue = isSelectAllOperators ? [] : (selectedOperators || []);
    const hasActiveAgentFilter = !isSelectAllAgents;
    const hasActiveVehicleFilter = !isSelectAllVehicles;
    const hasActiveOperatorFilter = !isSelectAllOperators;

    return (
        <div className="refine-bar">
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by agent</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allAgents.length === 0 ? 'No agents in result' : 'All agents'}
                    value={agentValue}
                    onChange={handleAgentChange}
                    maxTagCount={3}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allAgents.length === 0}
                    style={{ width: '100%' }}
                >
                    {allAgents.map(name => <Option key={name} value={name}>{name}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by vehicle</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allVehicleIds.length === 0 ? 'No vehicles in result' : 'All vehicles'}
                    value={vehicleValue}
                    onChange={handleVehicleChange}
                    maxTagCount={3}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="label"
                    filterOption={(input, option) => String(option.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allVehicleIds.length === 0}
                    style={{ width: '100%' }}
                >
                    {(vehicleOptions || []).map(v => (
                        <Option key={v.id} value={v.id} label={v.label}>{v.label}</Option>
                    ))}
                </Select>
            </div>
            {showOperatorFilter && (
                <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                    <div className="field-label">Filter by operator</div>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="All operators"
                        value={operatorValue}
                        onChange={handleOperatorChange}
                        maxTagCount={3}
                        maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                        optionFilterProp="children"
                        filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        style={{ width: '100%' }}
                    >
                        {allOperators.map(name => <Option key={name} value={name}>{name}</Option>)}
                    </Select>
                </div>
            )}
            {(hasActiveAgentFilter || hasActiveVehicleFilter || hasActiveOperatorFilter) && (
                <button type="button" className="btn" onClick={() => {
                    if (hasActiveAgentFilter) handleAgentChange([]);
                    if (hasActiveVehicleFilter) handleVehicleChange([]);
                    if (hasActiveOperatorFilter) handleOperatorChange([]);
                }}>Clear</button>
            )}
        </div>
    );
}

function FilterSummaryChips({ filters, generatedAt, vehicleNumberById, scheduleLabel, hasReport }) {
    if (!hasReport) return null;
    const dateLabel = filters.fromDate && filters.toDate ? `${filters.fromDate} – ${filters.toDate}` : '—';
    const agentLabel = filters.isSelectAllAgents ? 'All' :
        (isValidArray(filters.agentName) ? (filters.agentName.length <= 2 ? filters.agentName.join(', ') : `${filters.agentName.length} selected`) : 'All');
    const vehicleLabel = filters.isSelectAllVehicles ? 'All'
        : (isValidArray(filters.vehicleNumbers) && filters.vehicleNumbers.length > 0
            ? (filters.vehicleNumbers.length <= 2
                ? filters.vehicleNumbers.map(id => vehicleNumberById[id] || id).join(', ')
                : `${filters.vehicleNumbers.length} selected`)
            : 'All');
    return (
        <div className="filter-summary">
            <span className="chip"><span className="label">{filters.isBookingDate ? 'Booking' : 'Travel'}</span><strong>{dateLabel}</strong></span>
            <span className="chip"><span className="label">Schedule</span><strong>{scheduleLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Agent</span><strong>{agentLabel}</strong></span>
            <span className="chip"><span className="label">Vehicle</span><strong>{vehicleLabel}</strong></span>
            {!filters.isSelectAllOperators && isValidArray(filters.operatorNames) &&
                <span className="chip"><span className="label">Operator</span><strong>{filters.operatorNames.length <= 2 ? filters.operatorNames.join(', ') : `${filters.operatorNames.length} selected`}</strong></span>
            }
            {generatedAt && <span className="gen">Generated {generatedAt}</span>}
        </div>
    );
}

function HeroMetric({ netCommission, fromCustomers, grossCommission, confirmedCount, cancelledCount }) {
    return (
        <div className="hero-metric">
            <div>
                <div className="l">Net Commission Earned</div>
                <div className="v">{fmtINRpaise(netCommission)}</div>
                <div className="hero-counts">
                    <span className="hero-count confirmed">
                        <strong>{Number(confirmedCount || 0).toLocaleString('en-IN')}</strong> confirmed
                    </span>
                    <span className="hero-count cancelled">
                        <strong>{Number(cancelledCount || 0).toLocaleString('en-IN')}</strong> cancelled
                    </span>
                </div>
                <div className="formula">Commission earned (F) − TDS deducted (G)</div>
            </div>
            <div className="right">
                <div className="pair">
                    <div className="stat">
                        <div className="l">From customers</div>
                        <div className="v">{fmtINRpaise(fromCustomers)}</div>
                        <div className="sub">Collected on partner trips</div>
                    </div>
                    <div className="stat">
                        <div className="l">Gross commission</div>
                        <div className="v">{fmtINRpaise(grossCommission)}</div>
                        <div className="sub">Before TDS</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SummaryTable({ rows }) {
    return (
        <div className="summary-table">
            <div className="h">Metric</div>
            <div className="h right">Confirmed</div>
            <div className="h right">Cancelled</div>
            <div className="h right">Relevant</div>
            {rows.map(r => (
                <div key={r.code} className={`row ${r.subtotal ? 'subtotal' : ''}`}>
                    <div className="cell metric"><span className="code">{r.code}.</span>{r.metric}</div>
                    <div className="cell value">{fmtINR(r.confirmed)}</div>
                    <div className="cell value">{fmtINR(r.cancelled)}</div>
                    <div className="cell value">{fmtINR(r.relevant)}</div>
                </div>
            ))}
        </div>
    );
}

function SummaryCard({ filteredConfirmed, filteredCancelled, confirmedCount, cancelledCount }) {
    const sum = useMemo(
        () => buildSummaryRows(filteredConfirmed, filteredCancelled),
        [filteredConfirmed, filteredCancelled]
    );
    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Commission Summary</div>
            </div>
            <HeroMetric
                netCommission={sum.netCommissionRelevant}
                fromCustomers={sum.fromCustomers}
                grossCommission={sum.grossCommission}
                confirmedCount={confirmedCount}
                cancelledCount={cancelledCount}
            />
            <div className="summary-section">
                <div className="summary-section-title">Revenue Ledger (Partner Trips)</div>
                <SummaryTable rows={sum.revenue} />
            </div>
            <div className="summary-section">
                <div className="summary-section-title">Commissions</div>
                <SummaryTable rows={sum.commissions} />
            </div>
        </div>
    );
}

function TxDetailGrid({ record, type, vehicleNumberById }) {
    const tripDescription = record.description || '';
    const seatNumberStr = record.seat_number || '';
    const seatCount = record.seat_count || 0;
    const vehicle = vehicleNumberById[record.vehicle_id] || record.vehicle_number || '—';
    const bookingTime = record.booking_time ? getLocalDateAndTime(record.booking_time) : '—';
    const passenger = record.full_name || '—';
    const phone = record.phone_number || '—';

    const ticketFare = record.fare || 0;
    const gst = AFP(record.cgst || 0, record.sgst || 0);
    const total = record.ticket_cost || 0;
    const discount = record.direct_discount || 0;
    const amountPaid = record.amountPaid || 0;
    const commission = record.commission || 0;
    const tds = record.tds || 0;
    const penalty = record.cancellation_penalty || 0;
    const netCommission = SFP(commission, tds);
    const refundAmount = amountPaid - penalty;

    return (
        <div className="tx-detail-grid">
            <div className="tx-detail-section">
                <div className="l">Booking & Trip</div>
                <div className="detail-row"><span className="k">Operator</span><span className="v text">{record.operator_name || '—'}</span></div>
                <div className="detail-row"><span className="k">Booking time</span><span className="v">{bookingTime}</span></div>
                <div className="detail-row"><span className="k">Passenger</span><span className="v text">{passenger}</span></div>
                <div className="detail-row"><span className="k">Phone</span><span className="v">{phone}</span></div>
                <div className="detail-row"><span className="k">Vehicle</span><span className="v">{vehicle}</span></div>
                {tripDescription && <div className="detail-row"><span className="k">Trip</span><span className="v text">{tripDescription}</span></div>}
                <div className="detail-row"><span className="k">Seats ({seatCount})</span><span className="v">{seatNumberStr || '—'}</span></div>
                {record.cancelled_by && <div className="detail-row"><span className="k">Cancelled by</span><span className="v text">{record.cancelled_by}</span></div>}
            </div>
            <div className="tx-detail-section">
                <div className="l">Financial Breakdown</div>
                <div className="detail-row"><span className="k">Ticket fare (A)</span><span className="v">{fmtINRpaise(ticketFare)}</span></div>
                <div className="detail-row"><span className="k">GST (B)</span><span className="v">{fmtINRpaise(gst)}</span></div>
                <div className="detail-row"><span className="k">Total amount (A+B)</span><span className="v">{fmtINRpaise(total)}</span></div>
                {type === 'confirmed' ? (
                    <>
                        <div className="detail-row"><span className="k">Discount (D)</span><span className="v">{fmtINRpaise(discount)}</span></div>
                        <div className="detail-row"><span className="k">Amount paid (A+B−D)</span><span className="v">{fmtINRpaise(amountPaid)}</span></div>
                        <div className="detail-row"><span className="k">Commission (F)</span><span className="v">{fmtINRpaise(commission)}</span></div>
                        <div className="detail-row"><span className="k">TDS (G)</span><span className="v">{fmtINRpaise(tds)}</span></div>
                        <div className="detail-row bold"><span className="k">Net commission (F − G)</span><span className="v">{fmtINRpaise(netCommission)}</span></div>
                    </>
                ) : (
                    <>
                        <div className="detail-row"><span className="k">Penalty</span><span className="v">{fmtINRpaise(penalty)}</span></div>
                        <div className="detail-row bold"><span className="k">Refund to customer</span><span className="v">{fmtINRpaise(refundAmount)}</span></div>
                    </>
                )}
            </div>
        </div>
    );
}

const computeNetOrRefund = (record, isConfirmed) => {
    if (isConfirmed) return SFP(record.commission || 0, record.tds || 0);
    return (record.amountPaid || 0) - (record.cancellation_penalty || 0);
};

// ----- Display columns for the Transactions table -----
const buildDisplayColumns = ({ type, agentFilters, subAgentFilters, showSubAgent, operatorFilters }) => {
    const isConfirmed = type === 'confirmed';
    const cols = [
        {
            title: 'PNR', key: 'pnr', dataIndex: 'pnr',
            render: (text) => <span className="tx-pnr">{text || '—'}</span>,
        },
        {
            title: 'Operator', key: 'operator_name', dataIndex: 'operator_name',
            filters: operatorFilters,
            onFilter: (value, record) => record.operator_name === value,
            render: (text) => <span className="tx-agent"><span className="dot ch-web"></span>{text || '—'}</span>,
        },
        {
            title: 'Agent', key: 'name', dataIndex: 'name',
            filters: agentFilters,
            onFilter: (value, record) => record.name === value,
            render: (text) => (
                <span className="tx-agent">
                    <span className={`dot ${getChannelClass(text)}`}></span>{text || '—'}
                </span>
            ),
        },
        ...(showSubAgent
            ? [{
                title: 'Sub Agent', key: 'sub_agent_name', dataIndex: 'sub_agent_name',
                filters: subAgentFilters,
                onFilter: (value, record) => record.sub_agent_name === value,
                render: (text) => <span className="tx-sub-agent">{text || '—'}</span>,
            }]
            : []),
        {
            title: 'Route', key: 'route',
            render: (_t, record) => (
                <div className="tx-route">
                    <div className="ot">{record.origin || '—'}<span className="arrow">→</span>{record.destination || '—'}</div>
                    {record.full_name && <div className="name">{record.full_name}</div>}
                </div>
            ),
        },
        {
            title: 'Travel', key: 'start_date',
            render: (_t, record) => {
                if (isValid(record.start_date)) {
                    return <span className="tx-date">{getLocalDate(convertDateStringToDate(record.start_date.split('T')[0]))}</span>;
                }
                return <span className="tx-date">—</span>;
            },
        },
        {
            title: 'Seats', key: 'seat_number', dataIndex: 'seat_number',
            render: (text) => {
                const seats = String(text || '').split(';').map(s => s.trim()).filter(Boolean);
                if (seats.length === 0) return <span className="tx-seat-pill">—</span>;
                return <div className="tx-seats">{seats.map(s => <span key={s} className="tx-seat-pill">{s}</span>)}</div>;
            },
        },
        {
            title: 'Gender', key: 'gender', dataIndex: 'gender',
            filters: [
                { text: 'Male', value: 'M' },
                { text: 'Female', value: 'F' },
            ],
            onFilter: (value, record) => String(record.gender || '').includes(value),
            render: (text) => text || '—',
        },
        {
            title: 'Status', key: 'travellerStatus', dataIndex: 'travellerStatus',
            render: (_t, record) => {
                const statusValues = Array.isArray(record.travellerStatus)
                    ? record.travellerStatus.filter(Boolean)
                    : (record.travellerStatus ? [record.travellerStatus] : []);
                const someCancelled = statusValues.some(s => String(s).toLowerCase().includes('cancel'));
                const someNonCancelled = statusValues.some(s => !String(s).toLowerCase().includes('cancel'));
                let cls, label;
                if (someCancelled && someNonCancelled) {
                    cls = 'partial';
                    label = 'Partially cancelled';
                } else if (someCancelled || (statusValues.length === 0 && !isConfirmed)) {
                    cls = 'cancelled';
                    label = statusValues.length > 0 ? statusValues.join(', ') : 'Cancelled';
                } else {
                    cls = 'confirmed';
                    label = statusValues.length > 0 ? statusValues.join(', ') : 'Confirmed';
                }
                return <span className={`tx-status ${cls}`}>{label}</span>;
            },
        },
        {
            title: isConfirmed ? 'Net commission' : 'Refund',
            key: isConfirmed ? 'net_commission_display' : 'refund_amount_display',
            align: 'right',
            className: 'col-amount',
            sorter: (a, b) => computeNetOrRefund(a, isConfirmed) - computeNetOrRefund(b, isConfirmed),
            render: (_t, record) => (
                <span className="tx-amount">{fmtINR(computeNetOrRefund(record, isConfirmed))}</span>
            ),
        },
        ...(global.useRemarks === 1
            ? [{
                title: 'Remarks', key: 'remarks', dataIndex: 'remarks',
                render: (text) => <span className="tx-remarks">{text || '—'}</span>,
            }]
            : []),
    ];
    return cols;
};

function TransactionsCard({
    confirmed, cancelled, vehicleNumberById,
    confirmPaginationSize, setConfirmPaginationSize, setConfirmPage,
    cancelPaginationSize, setCancelPaginationSize, setCancelPage,
}) {
    const [tab, setTab] = useState('confirmed');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('travel_recent');

    const filterAndSort = (rows, type) => {
        if (!isValidArray(rows)) return [];
        const q = search.trim().toLowerCase();
        let r = rows;
        if (q) {
            r = r.filter(x => {
                const fields = [x.pnr, x.full_name, x.name, x.operator_name, x.phone_number, x.origin, x.destination, vehicleNumberById[x.vehicle_id]];
                return fields.some(f => f && String(f).toLowerCase().includes(q));
            });
        }
        const sorted = [...r];
        switch (sort) {
            case 'travel_recent':
                sorted.sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || ''))); break;
            case 'amount_high':
                sorted.sort((a, b) => computeNetOrRefund(b, type === 'confirmed') - computeNetOrRefund(a, type === 'confirmed')); break;
            case 'pnr':
                sorted.sort((a, b) => String(a.pnr || '').localeCompare(String(b.pnr || ''))); break;
            default: break;
        }
        return sorted;
    };

    const visibleConfirmed = filterAndSort(confirmed, 'confirmed');
    const visibleCancelled = filterAndSort(cancelled, 'cancelled');

    const data = tab === 'confirmed' ? visibleConfirmed : visibleCancelled;
    const agentFilters = useMemo(() => getAgentFilterOptions(data), [data]);
    const subAgentFilters = useMemo(() => getSubAgentFilterOptions(data), [data]);
    const operatorFilters = useMemo(() => getOperatorFilterOptions(data), [data]);
    const showSubAgent = useMemo(
        () => [...(confirmed || []), ...(cancelled || [])].some(t => isValid(t.sub_agent_name)),
        [confirmed, cancelled]
    );
    const columns = useMemo(
        () => buildDisplayColumns({ type: tab, agentFilters, subAgentFilters, showSubAgent, operatorFilters }),
        [tab, agentFilters, subAgentFilters, showSubAgent, operatorFilters]
    );

    const pagination = tab === 'confirmed'
        ? getPaginationDetails(true, setConfirmPage, confirmPaginationSize, setConfirmPaginationSize)
        : getPaginationDetails(true, setCancelPage, cancelPaginationSize, setCancelPaginationSize);

    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Transactions</div>
                <div className="tabs">
                    <button type="button" className={tab === 'confirmed' ? 'active' : ''} onClick={() => setTab('confirmed')}>
                        Confirmed <span className="count">{Number(visibleConfirmed.length).toLocaleString('en-IN')}</span>
                    </button>
                    <button type="button" className={tab === 'cancelled' ? 'active' : ''} onClick={() => setTab('cancelled')}>
                        Cancelled <span className="count">{Number(visibleCancelled.length).toLocaleString('en-IN')}</span>
                    </button>
                </div>
            </div>
            <div className="card-sub">
                {tab === 'confirmed'
                    ? 'Tickets booked by your agents on partner operators\' shared trips. Tap a row to see the full financial breakdown.'
                    : 'Tickets that were cancelled. Penalty and refund details available on expand.'}
            </div>

            <div className="table-toolbar">
                <div className="search">
                    {Icon.search}
                    <input
                        placeholder="Search by PNR, name, phone, city, operator…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="table-toolbar-right">
                    <span>Sort:</span>
                    <Select
                        value={sort}
                        onChange={setSort}
                        size="small"
                        style={{ width: 200 }}
                        bordered={true}
                    >
                        <Option value="travel_recent">Travel date (recent)</Option>
                        <Option value="amount_high">Highest commission</Option>
                        <Option value="pnr">PNR</Option>
                    </Select>
                </div>
            </div>

            <Table
                className="tx-table"
                rowKey={(r, idx) => `${r.pnr || ''}-${r.trip_id || ''}-${r.seat_number || ''}-${idx}`}
                columns={columns}
                dataSource={data}
                pagination={pagination}
                expandable={{
                    expandedRowRender: (record) => <TxDetailGrid record={record} type={tab} vehicleNumberById={vehicleNumberById} />,
                    expandIconColumnIndex: -1,
                    expandRowByClick: true,
                    rowExpandable: () => true,
                }}
                locale={{ emptyText: <div className="empty-state">No {tab} transactions match the current filters.</div> }}
                size="middle"
            />
        </div>
    );
}

// ============================================================
// Main component
// ============================================================
export const SharedTripSalesReport = () => {
    const [form] = Form.useForm();

    const [schedules, setSchedules] = useState(undefined);
    const [showReport, setShowReport] = useState(false);
    const [running, setRunning] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [confirmedTickets, setConfirmedTickets] = useState(undefined);
    const [cancelledTickets, setCancelledTickets] = useState(undefined);
    const [vehicleNumberById, setVehicleNumberById] = useState({});
    const [dateType, setDateType] = useState(false);     // false = Travel, true = Booking
    const [filters, setFilters] = useState({
        fromDate: undefined, toDate: undefined,
        isBookingDate: false, scheduleId: undefined, isSelectAllSchedules: true,
        agentNames: [], agentName: [], isSelectAllAgents: true,
        vehicleNumbers: [], isSelectAllVehicles: true,
        operatorNames: [], isSelectAllOperators: true,
    });

    const [confirmPage, setConfirmPage] = useState(1);
    const [cancelPage, setCancelPage] = useState(1);
    const [confirmPaginationSize, setConfirmPaginationSize] = useState(getDefaultPageSize);
    const [cancelPaginationSize, setCancelPaginationSize] = useState(getDefaultPageSize);

    const lastDateKeyRef = useRef('');
    const cachedSheetRef = useRef(undefined);

    const deriveSchedules = (sheet) => {
        const byId = {};
        [(sheet && sheet.confirmedTransactions) || [], (sheet && sheet.cancelledTransactions) || []].forEach((rows) => {
            rows.forEach((row) => {
                if (isValid(row.schedule_id) && !byId[row.schedule_id]) {
                    byId[row.schedule_id] = { id: row.schedule_id, description: `${row.description} (${row.operator_name})` };
                }
            });
        });
        return Object.values(byId);
    };

    // Lazy sheet fetch when date range / date type changes — also populates the schedule dropdown.
    const refetchSheetIfNeeded = (dateRange, isBooking) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        const key = `${fromDate}|${toDate}|${!!isBooking}`;
        if (key === lastDateKeyRef.current) return;
        lastDateKeyRef.current = key;
        fetchSharedTripSalesSheet(fromDate, toDate, !!isBooking, (sheet) => {
            cachedSheetRef.current = sheet;
            setSchedules(deriveSchedules(sheet));
        }, () => { setSchedules([]); cachedSheetRef.current = undefined; });
    };

    // Pre-load vehicle id → number map on mount so display lookups work even before first run.
    useEffect(() => {
        getVechicles((vehicleData) => {
            setVehicleNumberById(vehicleData.reduce((acc, v) => { acc[v.id] = v.vechicle_no; return acc; }, {}));
        });
    }, []);

    const onValuesChange = (changed, all) => {
        if ('dateRange' in changed || 'isBooking' in changed) {
            refetchSheetIfNeeded(all.dateRange, all.isBooking);
        }
    };

    const onRunReport = (values) => {
        const { dateRange, isBooking, returnId } = values;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setShowReport(false); setRunning(true);

        const ensureSheet = () => new Promise((resolve, reject) => {
            const key = `${fromDate}|${toDate}|${!!isBooking}`;
            if (key === lastDateKeyRef.current && isValid(cachedSheetRef.current)) {
                return resolve(cachedSheetRef.current);
            }
            lastDateKeyRef.current = key;
            fetchSharedTripSalesSheet(fromDate, toDate, !!isBooking, (sheet) => {
                cachedSheetRef.current = sheet;
                setSchedules(deriveSchedules(sheet));
                resolve(sheet);
            }, reject);
        });

        ensureSheet().then((sheet) => {
            const isSelectAllSchedules = !isValid(returnId);
            const confirmed = getTicketsBySchedule(sheet.confirmedTransactions, returnId);
            const cancelled = getTicketsBySchedule(sheet.cancelledTransactions, returnId);
            const allAgents = [...new Set([...(confirmed || []), ...(cancelled || [])].map(t => t.name).filter(Boolean))];
            setFilters({
                fromDate, toDate, isBookingDate: !!isBooking,
                scheduleId: returnId, isSelectAllSchedules,
                agentNames: allAgents.map(name => ({ name })),
                agentName: allAgents,
                isSelectAllAgents: true,
                vehicleNumbers: [],
                isSelectAllVehicles: true,
                operatorNames: [],
                isSelectAllOperators: true,
            });
            setConfirmedTickets(confirmed);
            setCancelledTickets(cancelled);
            setGeneratedAt(getLocalDateAndTime(new Date()));
            setShowReport(true); setRunning(false);
        }).catch(() => { setRunning(false); message.error('Failed to load shared trip sales'); });
    };

    const onReset = () => {
        form.resetFields();
        setDateType(false);
        setSchedules(undefined);
        cachedSheetRef.current = undefined; lastDateKeyRef.current = '';
        setConfirmedTickets(undefined); setCancelledTickets(undefined);
        setShowReport(false); setGeneratedAt(null);
        setFilters({
            fromDate: undefined, toDate: undefined,
            isBookingDate: false, scheduleId: undefined, isSelectAllSchedules: true,
            agentNames: [], agentName: [], isSelectAllAgents: true,
            vehicleNumbers: [], isSelectAllVehicles: true,
            operatorNames: [], isSelectAllOperators: true,
        });
    };

    // Pass `undefined` to the helper when "All vehicles" is selected — same semantics as no filter.
    const activeVehicleFilter = filters.isSelectAllVehicles ? undefined : filters.vehicleNumbers;

    // Derive filtered tickets for the SummaryCard, table, and export.
    const filteredConfirmedTickets = useMemo(
        () => getTicketsByOperator(
            getTicketsByAgentName(confirmedTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter),
            filters.operatorNames, filters.isSelectAllOperators),
        [confirmedTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter, filters.operatorNames, filters.isSelectAllOperators]
    );
    const filteredCancelledTickets = useMemo(
        () => getTicketsByOperator(
            getTicketsByAgentName(cancelledTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter),
            filters.operatorNames, filters.isSelectAllOperators),
        [cancelledTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter, filters.operatorNames, filters.isSelectAllOperators]
    );

    // Operator options from the loaded rows.
    const operatorOptions = useMemo(() => {
        const names = new Set();
        [confirmedTickets, cancelledTickets].forEach((tickets) => {
            if (isValidArray(tickets)) {
                tickets.forEach((ticket) => { if (isValid(ticket.operator_name)) names.add(ticket.operator_name); });
            }
        });
        return Array.from(names).sort();
    }, [confirmedTickets, cancelledTickets]);

    const confirmedTicketCount = filteredConfirmedTickets.length;
    const cancelledTicketCount = filteredCancelledTickets.length;

    // Vehicle options derived from the loaded ticket data.
    const vehicleOptions = useMemo(() => {
        const ids = new Set();
        const visit = (rows) => {
            if (!isValidArray(rows)) return;
            rows.forEach(t => { if (t && t.vehicle_id != null) ids.add(t.vehicle_id); });
        };
        visit(confirmedTickets); visit(cancelledTickets);
        return [...ids]
            .map(id => ({ id, label: vehicleNumberById[id] || `Vehicle ${id}` }))
            .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [confirmedTickets, cancelledTickets, vehicleNumberById]);

    // ============ Column factories — used for Excel export ============
    const commonColumns = [
        { title: 'Origin', key: 'origin', dataIndex: 'origin', render: (text) => text },
        { title: 'Destination', key: 'destination', dataIndex: 'destination', render: (text) => text },
        { title: 'Vehicle Number', key: 'vehicle_number', dataIndex: 'vehicle_number', render: (_t, record) => vehicleNumberById[record.vehicle_id] },
        { title: 'Seat count', key: 'seat_count', dataIndex: 'seat_count', render: (text) => roundNumber(text) },
        { title: 'Ticket Fare(A)', key: 'fare', dataIndex: 'fare', render: (text) => roundNumber(text) },
        { title: 'GST(B)', key: 'gst', render: (_t, record) => AFP(record.cgst, record.sgst) },
        { title: 'Total amount', key: 'ticket_cost', dataIndex: 'ticket_cost', render: (text) => roundNumber(text) },
    ];
    const commonConfirmedTicketsColumns = [
        { title: 'Discount(D)', key: 'direct_discount', dataIndex: 'direct_discount', render: (text) => roundNumber(text) },
        {
            title: 'Amount Paid(A+B-D)', excelTitle: 'Amount paid by the customer(A-B)',
            key: 'amountPaid', dataIndex: 'amountPaid',
            render: (_t, record) => roundNumber(record.amountPaid),
        },
        { title: 'Commission(F)', key: 'commission', dataIndex: 'commission', render: (text) => roundNumber(text) },
        { title: 'TDS(G)', key: 'tds', dataIndex: 'tds', render: (text) => roundNumber(text) },
        {
            title: 'Net commission(F-G)', excelTitle: 'Net commission(Commission - TDS)',
            key: 'net_commision',
            render: (_t, record) => SFP(record.commission, record.tds),
        },
        { title: 'Trip description', key: 'description', dataIndex: 'description', render: (text) => text },
    ];

    const confirmTicketsColumns = [
        { title: 'SN', key: 'index', render: (_t, _r, idx) => (confirmPage - 1) * confirmPaginationSize + idx + 1 },
        {
            title: 'Travel date', key: 'travel_date',
            render: (_t, record) => isValid(record.start_date) ? getLocalDate(convertDateStringToDate(record.start_date.split('T')[0])) : '',
        },
        { title: 'PNR', key: 'pnr', dataIndex: 'pnr', render: (text) => text },
        { title: 'Operator', key: 'operator_name', dataIndex: 'operator_name', render: (text) => text },
        { title: 'Agent', key: 'name', dataIndex: 'name', render: (text) => text },
        { title: 'Booking time', key: 'booking_time', render: (_t, record) => getLocalDateAndTime(record.booking_time) },
        { title: 'Seat number', key: 'seat_number', dataIndex: 'seat_number', render: (text) => text },
        { title: 'Name', key: 'full_name', dataIndex: 'full_name', render: (text) => text },
        { title: 'Gender', key: 'gender', dataIndex: 'gender', render: (text) => text || '' },
        { title: 'Phone Number', key: 'phone_number', dataIndex: 'phone_number', render: (text) => text },
        {
            title: 'Status', key: 'travellerStatus', dataIndex: 'travellerStatus',
            render: (_t, record) => Array.isArray(record.travellerStatus) ? record.travellerStatus.join(', ') : (record.travellerStatus || ''),
        },
        ...commonColumns,
        ...commonConfirmedTicketsColumns,
        ...(global.useRemarks === 1
            ? [{ title: 'Remarks', key: 'remarks', dataIndex: 'remarks', render: (text) => text || '' }]
            : []),
    ];

    const commonCancelledTicketsColumns = [
        { title: 'Cancelled By', key: 'cancelled_by', dataIndex: 'cancelled_by', render: (text) => text || '' },
        { title: 'Penality', key: 'cancellation_penalty', dataIndex: 'cancellation_penalty', render: (text) => roundNumber(text) },
        { title: 'Commission(F)', key: 'commission', dataIndex: 'commission', render: (text) => roundNumber(text) },
        { title: 'TDS(G)', key: 'tds', dataIndex: 'tds', render: (text) => roundNumber(text) },
        {
            title: 'Net commision(F-G)', key: 'net_commision',
            render: (_t, record) => SFP(record.commission, record.tds),
        },
        {
            title: 'Refund amount', key: 'refund_amount',
            render: (_t, record) => {
                let notboardedTxt = "";
                if (isValidBool(record.boarded) && record.boarded == 0) notboardedTxt = ` (Not boarded)`;
                return `${roundNumber(record.amountPaid - record.cancellation_penalty)}${notboardedTxt}`;
            },
        },
        { title: 'Trip description', key: 'description', dataIndex: 'description', render: (text) => text },
    ];

    const cancelTicketsColumns = [
        { title: 'SN', key: 'index', render: (_t, _r, idx) => (cancelPage - 1) * cancelPaginationSize + idx + 1 },
        {
            title: 'Travel date', key: 'travel_date',
            render: (_t, record) => isValid(record.start_date) ? getLocalDate(convertDateStringToDate(record.start_date.split('T')[0])) : '',
        },
        { title: 'PNR', key: 'pnr', dataIndex: 'pnr', render: (text) => text },
        { title: 'Operator', key: 'operator_name', dataIndex: 'operator_name', render: (text) => text },
        { title: 'Agent', key: 'name', dataIndex: 'name', render: (text) => text },
        { title: 'Booking time', key: 'booking_time', render: (_t, record) => getLocalDateAndTime(record.booking_time) },
        { title: 'Name', key: 'full_name', dataIndex: 'full_name', render: (text) => text },
        { title: 'Gender', key: 'gender', dataIndex: 'gender', render: (text) => text || '' },
        { title: 'Phone Number', key: 'phone_number', dataIndex: 'phone_number', render: (text) => text },
        ...commonColumns,
        ...commonCancelledTicketsColumns,
        ...(global.useRemarks === 1
            ? [{ title: 'Remarks', key: 'remarks', dataIndex: 'remarks', render: (text) => text || '' }]
            : []),
    ];

    const onExport = () => {
        generateJsonToExport(filters, filteredConfirmedTickets, filteredCancelledTickets, confirmTicketsColumns, cancelTicketsColumns);
    };

    const scheduleLabel = (() => {
        if (filters.isSelectAllSchedules || !isValid(filters.scheduleId)) return 'All';
        if (isValidArray(schedules)) {
            const found = schedules.find(s => s && s.id === filters.scheduleId);
            return found?.description || 'Selected';
        }
        return 'Selected';
    })();

    return (
        <div className="report-inner-div sales-report-v2">
            <PageHeader onExport={onExport} canExport={showReport} />
            <FilterCard
                form={form}
                schedules={schedules}
                dateType={dateType}
                setDateType={(v) => { setDateType(v); refetchSheetIfNeeded(form.getFieldValue('dateRange'), v); }}
                onSubmit={onRunReport}
                onReset={onReset}
                onValuesChange={onValuesChange}
                running={running}
            />
            {showReport && (
                <RefineBar
                    agentOptions={filters.agentNames}
                    selectedAgents={filters.agentName}
                    isSelectAllAgents={filters.isSelectAllAgents}
                    onAgentChange={({ agentName, isSelectAllAgents }) => {
                        setFilters((f) => ({ ...f, agentName, isSelectAllAgents }));
                    }}
                    vehicleOptions={vehicleOptions}
                    selectedVehicles={filters.vehicleNumbers}
                    isSelectAllVehicles={filters.isSelectAllVehicles}
                    onVehicleChange={({ vehicleNumbers, isSelectAllVehicles }) => {
                        setFilters((f) => ({ ...f, vehicleNumbers, isSelectAllVehicles }));
                    }}
                    operatorOptions={operatorOptions}
                    selectedOperators={filters.operatorNames}
                    isSelectAllOperators={filters.isSelectAllOperators}
                    onOperatorChange={({ operatorNames, isSelectAllOperators }) => {
                        setFilters((f) => ({ ...f, operatorNames, isSelectAllOperators }));
                    }}
                />
            )}
            <FilterSummaryChips
                filters={filters}
                generatedAt={generatedAt}
                vehicleNumberById={vehicleNumberById}
                scheduleLabel={scheduleLabel}
                hasReport={showReport}
            />
            {showReport && (
                <SummaryCard
                    filteredConfirmed={filteredConfirmedTickets}
                    filteredCancelled={filteredCancelledTickets}
                    confirmedCount={confirmedTicketCount}
                    cancelledCount={cancelledTicketCount}
                />
            )}
            {showReport && (
                <TransactionsCard
                    confirmed={filteredConfirmedTickets}
                    cancelled={filteredCancelledTickets}
                    vehicleNumberById={vehicleNumberById}
                    confirmPaginationSize={confirmPaginationSize}
                    setConfirmPaginationSize={setConfirmPaginationSize}
                    setConfirmPage={setConfirmPage}
                    cancelPaginationSize={cancelPaginationSize}
                    setCancelPaginationSize={setCancelPaginationSize}
                    setCancelPage={setCancelPage}
                />
            )}
        </div>
    );
};
